/**
 * PaymentColorsCard - Kalender-Farben (Payment-Status)
 * Eigenständig: lädt/speichert Farben für die Kalenderansicht.
 * Teil des Bereichs "Öffnungszeiten & Kalender".
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Save } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentColorsCard = () => {
  const [paymentColors, setPaymentColors] = useState({ paid: '#10b981', overdue: '#ef4444' });

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/settings/payment-colors`);
        if (res.data) setPaymentColors((c) => ({ ...c, ...res.data }));
      } catch (e) {
        // Defaults verwenden
      }
    })();
  }, []);

  const handleSave = async () => {
    try {
      await axios.put(`${API}/settings/payment-colors`, paymentColors);
      toast.success('Farben gespeichert! Bitte Termine-Seite neu laden (F5) um Änderungen zu sehen.');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  return (
    <Card data-testid="payment-colors-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">Kalender-Farben</CardTitle>
        <CardDescription>
          Farben für Payment Status im Kalender (Legende unten rechts)
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label>Gebucht (unbezahlt)</Label>
              <div className="bg-gray-50 p-4 rounded-lg border">
                <p className="text-sm text-gray-600">
                  Unbezahlte Termine zeigen die <strong>Farbe des jeweiligen Services</strong>.
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  → Service-Farben können unter "Dienstleistungen" bearbeitet werden.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Bezahlt</Label>
              <div className="flex gap-2 items-center">
                <Input
                  type="color"
                  value={paymentColors.paid}
                  onChange={(e) => setPaymentColors({ ...paymentColors, paid: e.target.value })}
                  className="w-24 h-10"
                  data-testid="payment-color-paid"
                />
                <div className="flex-1 h-10 rounded border-2" style={{ backgroundColor: paymentColors.paid }} />
                <span className="text-sm text-gray-600">{paymentColors.paid}</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Überfällig</Label>
              <div className="flex gap-2 items-center">
                <Input
                  type="color"
                  value={paymentColors.overdue}
                  onChange={(e) => setPaymentColors({ ...paymentColors, overdue: e.target.value })}
                  className="w-24 h-10"
                  data-testid="payment-color-overdue"
                />
                <div className="flex-1 h-10 rounded border-2" style={{ backgroundColor: paymentColors.overdue }} />
                <span className="text-sm text-gray-600">{paymentColors.overdue}</span>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Hinweis:</strong> Nach dem Speichern bitte die Termine-Seite neu laden (F5) um die neuen Farben zu sehen.
            </p>
          </div>

          <Button onClick={handleSave} className="w-full" data-testid="payment-colors-save-btn">
            <Save size={16} className="mr-2" />
            Farben speichern
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PaymentColorsCard;
